/**
 * Wallet (stored balance) as served by prepareit-backend's `/proxy-wallet/*`
 * and `/wallet/top-up`. Not to be confused with the Apple/Google Wallet loyalty
 * pass (`WalletUIConfigs`, `useAddToWallet`).
 *
 * The proxy passes the wallet microservice's envelope through untouched, and
 * that service sends money as strings. The `Api*` types describe the wire; the
 * rest are what the app works with, produced by walletParsers.
 */

type ApiEnvelope<T> = {
  success: boolean;
  data: T;
  timestamp: string;
};

export type ApiWalletCustomer = {
  customerId: string;
  accountId: string;
  totalBalance: string;
  availableBalance: string;
  heldBalance: string;
  currency: string;
  updatedAt: string;
};

export type ApiWalletCustomerResponse = ApiEnvelope<ApiWalletCustomer>;

type ApiWalletPresetIncentiveAction = {
  actionId: string;
  metadata: {
    msg?: string;
    /** "fixed" (flat points) or "percentage"/"percent" (points per 100 spent). */
    pointsType?: string;
    points?: number | string;
    [key: string]: unknown;
  };
};

export type ApiWalletPreset = {
  id: string;
  from: string | number;
  to: string | number;
  /** Cashback percent credited on top of the top-up, e.g. "20" = 20 %. */
  creditMultiplier: string | number | null;
  incentiveActions: ApiWalletPresetIncentiveAction[] | null;
};

export type ApiWalletPresetsResponse = ApiEnvelope<{
  currency: string;
  minTopUpAmount: number;
  /**
   * The highest top-up the merchant accepts (its wallet calibration); null or
   * absent without one.
   */
  maxTopUpAmount?: string | number | null;
  /**
   * The amounts the merchant offers, ascending, from its live wallet
   * calibration; null or absent without one (each tier's `from` is offered).
   */
  presetAmounts?: (string | number)[] | null;
  presets: ApiWalletPreset[];
}>;

/**
 * The backend types only the first six fields; the wallet service also sends
 * `balancesAfter` and `metadata`, which the proxy forwards untyped. `metadata`
 * can arrive JSON-encoded.
 */
export type ApiWalletTransaction = {
  transactionId: string;
  type: string;
  amount: string | number;
  currency: string;
  referenceId: string;
  createdAt: string;
  balancesAfter?: { availableBalance?: string | number };
  balanceAfter?: string | number;
  metadata?: ApiWalletTransactionMetadata | string | null;
};

export type ApiWalletTransactionMetadata = {
  topUp?: {
    topUpId?: string;
    points?: number | string;
    paidAmount?: number | string;
    cashbackAmount?: number | string;
  };
  capture?: { receiptId?: number | string };
  refund?: { receiptId?: number | string };
};

export type ApiWalletTransactionsResponse = ApiEnvelope<{
  items: ApiWalletTransaction[];
  nextCursor?: string;
  hasMore: boolean;
}>;

export type WalletTransactionsParams = {
  restaurantId: number;
  limit?: number;
  cursor?: string;
};

export type WalletTopUpBody = {
  franchiseId: number;
  amount: number;
  /** Required by the body type but ignored for Paymob top-ups: the backend
   * redirects to `/pay-paymentInvoice` / `/paymentfailed-paymentInvoice`. */
  successUrl: string;
  cancelUrl: string;
  points?: number;
  cashback?: number;
};

export type WalletTopUpResponse = {
  paymentMethod: string;
  payment_provider: string;
  payment_session_id: string;
  payment_link: string;
  paymentInvoiceId: number;
  paymentUuid: string;
};

// ---------------------------------------------------------------------------

export type WalletSummary = {
  availableBalance: number;
  totalBalance: number;
  heldBalance: number;
  currency: string;
};

/**
 * Ledger entry types as the wallet service sends them. HOLD / UNHOLD are
 * internal steps of paying with the wallet and are never shown.
 */
export enum WalletApiTransactionType {
  CAPTURE = "CAPTURE",
  TOPUP = "TOPUP",
  TOP_UP = "TOP_UP",
  REFUND = "REFUND",
  HOLD = "HOLD",
  UNHOLD = "UNHOLD",
}

export enum WalletTransactionType {
  PURCHASE = "PURCHASE",
  TOP_UP = "TOP_UP",
  REFUND = "REFUND",
  UNKNOWN = "UNKNOWN",
}

export type WalletTransaction = {
  id: string;
  type: WalletTransactionType;
  amount: number;
  currency: string;
  /** Available balance right after this entry, when the service sent it. */
  balanceAfter: number | null;
  createdAt: string;
  /** The ledger's reference; the app shows it as the order id when the
   * capture / refund metadata carries no receipt id. */
  referenceId: string | null;
  receiptId: number | null;
  topUp: {
    topUpId: string | null;
    paidAmount: number;
    cashbackAmount: number;
    points: number;
  } | null;
};

export type WalletTransactionsPage = {
  items: WalletTransaction[];
  nextCursor: string | null;
  hasMore: boolean;
};

/** One top-up tier: amounts in [from, to] earn cashback or points. */
export type WalletTopUpTier = {
  id: string;
  from: number;
  to: number;
  /** Server-authored label for the tier, when one is configured. */
  message: string | null;
  incentive:
    | { kind: "cashback"; percent: number }
    | { kind: "fixedPoints"; points: number }
    /** `pointsPer100` points for every 100 topped up. */
    | { kind: "percentagePoints"; pointsPer100: number };
};

export type WalletPresets = {
  currency: string;
  minTopUpAmount: number;
  /** `null` when the merchant set no maximum. */
  maxTopUpAmount: number | null;
  /** The merchant's own preset amounts; `null` to offer each tier's `from`. */
  presetAmounts: number[] | null;
  tiers: WalletTopUpTier[];
};

/** A preset card: an amount to top up and the tier it earns under, if any. */
export type WalletTopUpOffer = {
  id: string;
  amount: number;
  tier: WalletTopUpTier | null;
};

export type WalletTopUpIncentive = {
  cashback: number | null;
  points: number | null;
};
